'use client';

// HERO éditorial — parti pris : la landing parle la langue de l'APP (Fraunces, le serif du
// display mobile), pas celle d'un SaaS. Titre dominant, composition asymétrique, et à droite un
// souvenir qui se compose réellement sous les yeux du visiteur (MemoryGrid).
//
// Tailles de titre spécifiques à la landing (au-delà de l'échelle typo de l'app — usage
// marketing assumé, cf. Hero d'origine).

import Link from 'next/link';
import { ArrowRight, Play } from 'lucide-react';

import { useT } from '@/hooks/useT';
import { useReveal } from '@/hooks/useReveal';
import { fontStyle } from '@/theme/typography';
import { MemoryGrid } from './MemoryGrid';

export function Hero() {
  const { t } = useT('landing');
  const gridRef = useReveal<HTMLDivElement>({ threshold: 0.05 });

  return (
    // Pas de `.rmbr-texture` ici : son voile de grain (mix-blend-mode: overlay) se peint AU-DESSUS
    // du contenu non positionné et délave titre, boutons et tuiles. Le grain est réservé aux fonds
    // sombres (cf. Trust), où il apporte de la matière et où le contenu est explicitement en z-10.
    <section className="relative isolate overflow-hidden">
      {/* Atmosphère : deux lueurs colorées décalées, derrière le contenu. */}
      <div
        aria-hidden
        className="rmbr-pulse-glow pointer-events-none absolute -right-32 -top-40 -z-10 size-[42rem] rounded-full blur-3xl"
        style={{
          background:
            'radial-gradient(circle, var(--rmbr-brand-muted), transparent 68%)',
        }}
      />
      <div
        aria-hidden
        className="pointer-events-none absolute -bottom-56 -left-40 -z-10 size-[34rem] rounded-full opacity-60 blur-3xl"
        style={{
          background:
            'radial-gradient(circle, var(--rmbr-action-muted), transparent 70%)',
        }}
      />

      <div className="mx-auto grid w-full max-w-6xl items-center gap-12 px-5 pb-16 pt-12 sm:px-8 lg:grid-cols-[1fr_0.92fr] lg:gap-14 lg:pb-24 lg:pt-20">
        {/* ---------------------------------------------------------------- Colonne éditoriale */}
        <div className="flex flex-col items-start gap-6">
          <span
            className="rmbr-reveal inline-flex items-center gap-2 rounded-full border border-border bg-surface/70 px-3.5 py-1.5 text-text-secondary backdrop-blur"
            style={{ ...fontStyle('caption'), animationDelay: '40ms' }}
          >
            <span aria-hidden className="size-1.5 rounded-full bg-brand" />
            {t('hero.eyebrow')}
          </span>

          {/* Le titre est le personnage principal : serif éditorial, très grand, très serré. */}
          <h1
            className="rmbr-reveal text-balance text-text-primary"
            style={{
              animationDelay: '120ms',
              fontFamily: 'var(--font-serif)',
              fontSize: 'clamp(2.75rem, 6vw, 5rem)',
              lineHeight: 0.98,
              letterSpacing: '-0.035em',
              fontWeight: 600,
            }}
          >
            {t('hero.line1')}{' '}
            <em
              className="text-action"
              style={{ fontStyle: 'italic', fontWeight: 500 }}
            >
              {t('hero.accent')}
            </em>
            <br />
            {t('hero.line2')}
            <span
              aria-hidden
              className="rmbr-caret ml-1 inline-block h-[0.72em] w-[3px] translate-y-[0.02em] bg-brand align-baseline"
            />
          </h1>

          <p
            className="rmbr-reveal max-w-md text-pretty text-text-secondary"
            style={{ ...fontStyle('bodyLg'), animationDelay: '220ms' }}
          >
            {t('hero.subtitle')}
          </p>

          <div
            className="rmbr-reveal flex flex-wrap items-center gap-3"
            style={{ animationDelay: '320ms' }}
          >
            <Link
              href="/sign-up"
              style={fontStyle('button')}
              className="rmbr-lift group inline-flex h-12 items-center gap-2 rounded-full bg-action px-6 text-action-on shadow-[var(--rmbr-glow-action)] hover:bg-action-hover"
            >
              {t('hero.ctaPrimary')}
              <ArrowRight className="size-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
            <Link
              href="#tour"
              style={fontStyle('button')}
              className="rmbr-lift inline-flex h-12 items-center gap-2 rounded-full border border-border bg-surface/60 px-6 text-text-primary backdrop-blur hover:bg-fill-subtle"
            >
              <Play className="size-3.5 fill-current" />
              {t('hero.ctaSecondary')}
            </Link>
          </div>

          {/* Preuve immédiate : trois faits, pas trois adjectifs. */}
          <ul
            className="rmbr-reveal flex flex-wrap items-center gap-x-5 gap-y-2 text-text-tertiary"
            style={{ ...fontStyle('bodySm'), animationDelay: '400ms' }}
          >
            {(['free', 'noAds', 'privacy'] as const).map((key) => (
              <li key={key} className="flex items-center gap-2">
                <span aria-hidden className="size-1 rounded-full bg-brand" />
                {t(`hero.proof.${key}`)}
              </li>
            ))}
          </ul>
        </div>

        {/* --------------------------------------------------------------- Le souvenir composé */}
        <div className="relative w-full">
          <div
            aria-hidden
            className="pointer-events-none absolute -inset-6 -z-10 rounded-[2rem] opacity-70 blur-3xl"
            style={{
              background:
                'radial-gradient(60% 60% at 55% 40%, var(--rmbr-brand-muted), transparent 72%)',
            }}
          />
          <div ref={gridRef}>
            <MemoryGrid />
          </div>

          {/* On nomme ce que le visiteur regarde : ce n'est pas une image, c'est une vraie grille. */}
          <p
            className="mt-4 flex items-center justify-center gap-2 text-center text-text-tertiary"
            style={fontStyle('bodySm')}
          >
            <span aria-hidden className="h-px w-6 bg-border" />
            {t('hero.gridCaption')}
            <span aria-hidden className="h-px w-6 bg-border" />
          </p>
        </div>
      </div>
    </section>
  );
}
